'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRightIcon, MessageCircleIcon } from 'lucide-react';

import { MenuItem, MenuSubItem, NAV_CONFIG } from '@/app/(app)/sidebar/nav-config';
import { NavOfficeSwitcher } from '@/app/(app)/sidebar/nav-office-switcher';
import { NavUser } from '@/app/(app)/sidebar/nav-user';
import Logo from '@/components/logo';
import { useAuthCtx } from '@/components/providers/auth-provider';
import { ModuleName, useModulesCtx } from '@/components/providers/modules-provider';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';

type PermissionChecker = (
  item: MenuItem | MenuSubItem,
  parentModule?: ModuleName,
  parentRole?: 'superuser' | 'admin'
) => boolean;

export function AppSidebar() {
  const { isPermissionCovered } = useAuthCtx();
  const { isModuleEnabled } = useModulesCtx();
  const { office } = useOfficeCtx();

  const menuItems = useMemo(() => {
    const shouldShowItem = createPermissionChecker(isPermissionCovered, isModuleEnabled);

    return (Object.values(NAV_CONFIG) as MenuItem[])
      .map((item) => filterMenuSubItems(item, shouldShowItem))
      .filter((item): item is MenuItem => item !== null && shouldShowParentItem(item, shouldShowItem));
  }, [isPermissionCovered, isModuleEnabled]);

  return (
    <Sidebar variant='inset' className='pt-5'>
      <SidebarHeader className='flex h-6 flex-row items-center justify-between space-x-4 px-3 text-foreground'>
        <div className='flex items-center justify-between space-x-4'>
          <Link href='/'>
            <Logo className='h-6 w-6 text-primary' mode='animated' version='icon' />
          </Link>
        </div>
        <div className='text-sm font-semibold'>{office?.name}</div>
      </SidebarHeader>
      <SidebarContent className='mt-7'>
        <SidebarGroup className='list-none space-y-2 px-0'>
          {menuItems.map((item) => (
            <NavMenuItem key={item.key} item={item} />
          ))}
        </SidebarGroup>
      </SidebarContent>
      <SidebarMenuButton asChild className='feedback-button cursor-pointer'>
        <a>
          <MessageCircleIcon />
          <span>Feedback</span>
        </a>
      </SidebarMenuButton>
      <SidebarFooter className='px-0'>
        <NavOfficeSwitcher />
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}

function NavMenuItem({ item }: { item: MenuItem }) {
  const pathname = usePathname();
  const isActive = pathname === item.url;
  const [defaultOpen] = useState(() => {
    if (isActive) return true;
    if (item.items && Object.values(item.items).some((subItem) => subItem.url === pathname)) return true;
    return false;
  });

  return (
    <Collapsible asChild defaultOpen={defaultOpen}>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip={item.label} isActive={isActive}>
          {item.url ? (
            <Link href={item.url} key={item.label}>
              <item.icon />
              <span>{item.label}</span>
            </Link>
          ) : (
            <CollapsibleTrigger asChild>
              <div key={item.label}>
                <item.icon />
                <span>{item.label}</span>
              </div>
            </CollapsibleTrigger>
          )}
        </SidebarMenuButton>
        {item.items ? (
          <>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction className='data-[state=open]:rotate-90'>
                <ChevronRightIcon />
                <span className='sr-only'>Toggle</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub className='space-y-1 pt-1.5'>
                {Object.values(item.items).map((subItem) => (
                  <NavMenuSubItem key={subItem.key} item={subItem} isActive={subItem.url === pathname} />
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </>
        ) : null}
      </SidebarMenuItem>
    </Collapsible>
  );
}

function NavMenuSubItem({ item, isActive }: { item: MenuSubItem; isActive: boolean }) {
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive}>
        <Link href={item.url}>
          <item.icon />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

/**
 * Checks if a menu item should be visible based on role and module permissions.
 * Sub-items inherit parent requirements when they don't specify their own.
 */
function createPermissionChecker(
  isPermissionCovered: (role: 'superuser' | 'admin') => boolean,
  isModuleEnabled: (module: ModuleName) => boolean
): PermissionChecker {
  return (item, parentModule, parentRole) => {
    // Determine effective role requirement (own or inherited)
    const effectiveRole = item.requiredRole || parentRole;
    if (effectiveRole && !isPermissionCovered(effectiveRole)) {
      return false;
    }

    // Determine effective module requirement (own or inherited)
    const effectiveModule = item.requiredModule || parentModule;
    if (effectiveModule && !isModuleEnabled(effectiveModule)) {
      return false;
    }

    return true;
  };
}

/**
 * Filters sub-items of a menu based on permissions.
 * Returns null if all sub-items are filtered out (which will hide the parent too).
 */
function filterMenuSubItems(item: MenuItem, shouldShowItem: PermissionChecker): MenuItem | null {
  // Items without sub-items pass through as-is
  if (!item.items) return item;

  // Filter sub-items, passing parent's requirements for inheritance
  const filteredSubItems = Object.fromEntries(
    Object.entries(item.items).filter(([, subItem]) => shouldShowItem(subItem, item.requiredModule, item.requiredRole))
  );

  // Hide parent if no sub-items remain after filtering
  if (Object.keys(filteredSubItems).length === 0) {
    return null;
  }

  return { ...item, items: filteredSubItems };
}

/**
 * Determines if a parent menu item should be shown.
 * Parents with visible sub-items are shown regardless of their own permissions.
 * This allows granular sub-module access (e.g., settings:asset-management without base settings).
 */
function shouldShowParentItem(item: MenuItem, shouldShowItem: PermissionChecker): boolean {
  // Show parent if it has any visible sub-items
  if (item.items && Object.keys(item.items).length > 0) {
    return true;
  }

  // For items without sub-items (direct links), check their own permissions
  return shouldShowItem(item);
}
